import React, { useContext } from "react";
import Draggable from "react-draggable";
import MarketDepth from "../screens/OrdersPage/components/MarketDepth";
import WebsocketWrapperContext from "../context/WebsocketWrapperContext";

const MarketDepthModal = ({ isOpen, onClose, instrument, onBuySellClick, initialPosition, onPositionChange }) => {
  const { symbolData } = useContext(WebsocketWrapperContext);
  // Do not return before hooks; gate rendering later

  const code = instrument?.code;
  const marketData = code ? (symbolData[code] || {}) : {};
  const ltpPrice = marketData.ltp || 0;
  const pctChange = marketData.change_per || 0;
  const changeColor = pctChange >= 0 ? "text-p150" : "text-[#B01431]";
  // OHLC values
  const open = marketData.open || 0;
  const high = marketData.high || 0;
  const low = marketData.low || 0;
  const close = marketData.close || ltpPrice;

  const handleBuyClick = () => {
    if (onBuySellClick) {
      onBuySellClick(instrument, true);
    }
  };

  const handleSellClick = () => {
    if (onBuySellClick) {
      onBuySellClick(instrument, false);
    }
  };

  function formatNumber(value) {
    const integerValue = Math.floor(Number(value));
    const formattedValue = integerValue.toLocaleString("en-IN");

    return <span>{formattedValue}</span>;
  }

  const boxRef = React.useRef(null);
  const [defaultPos, setDefaultPos] = React.useState(null);

  React.useLayoutEffect(() => {
    if (!isOpen || !instrument) return;
    if (defaultPos != null) return;
    const computeCenter = () => {
      const box = boxRef.current;
      const w = box ? box.offsetWidth : 440; // approx max-w-md
      const h = box ? box.offsetHeight : 540;
      const x = Math.max(0, (window.innerWidth - w) / 2);
      const y = Math.max(0, (window.innerHeight - h) / 2);
      return { x, y };
    };
    const pos = initialPosition || computeCenter();
    setDefaultPos(pos);
    // Report initial geometry
    if (onPositionChange) {
      const box = boxRef.current;
      const width = box ? box.offsetWidth : 440;
      const height = box ? box.offsetHeight : 540;
      onPositionChange({ x: pos.x, y: pos.y, width, height });
    }
  }, [isOpen, instrument, initialPosition, onPositionChange, defaultPos]);

  if (!isOpen || !instrument || defaultPos == null) return null;

  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 1001 }}>
      <Draggable handle=".market-depth-modal-drag-handle" defaultPosition={defaultPos} onStop={(e, data) => {
        if (onPositionChange) {
          const box = boxRef.current;
          const width = box ? box.offsetWidth : 440;
          const height = box ? box.offsetHeight : 540;
          onPositionChange({ x: data.x, y: data.y, width, height });
        }
      }}>
      <div ref={boxRef} className="modal-box p-0 max-w-md bg-modalBg shadow-2xl shadow-black/30 rounded-lg overflow-hidden border border-n200 pointer-events-auto">
        <div className="p-4 flex justify-between items-center w-full border-b border-n200 market-depth-modal-drag-handle cursor-move">
          <div>
            {/* <h2 className="font-semibold text-base text-p300">Market depth</h2> */}
            <h2 className="font-semibold text-base text-p300">
              {instrument?.instrumentName ||
                instrument?.trading_symbol ||
                instrument?.name ||
                "Instrument"}
            </h2>
            <div className="mt-1 flex items-center">
              {/* <span className="text-xl font-bold text-p300">{ltpPrice}</span>
              <span className={`ml-2 text-xs font-medium ${changeColor}`}>
                {pctChange >= 0 ? "+" : ""}
                {pctChange}%
              </span> */}
            </div>
          </div>
          <form method="dialog">
            <button
              type="button"
              className="btn text-p300 btn-sm bg-p400 hover:bg-n100 btn-circle"
              onClick={onClose}>
              ✕
            </button>
          </form>
        </div>

        {/* OHLC Values */}
        <div className="px-4 py-3">
          <div className="grid grid-cols-4 gap-3 text-xs">
            <div className="flex justify-center flex-col items-center">
              <span className="text-n500">Open</span>
              <span className="text-p300 font-medium">{open}</span>
            </div>
            <div className="flex justify-center flex-col items-center">
              <span className="text-n500">High</span>
              <span className="text-p300 font-medium">{high}</span>
            </div>
            <div className="flex justify-center flex-col items-center">
              <span className="text-n500">Low</span>
              <span className="text-p300 font-medium">{low}</span>
            </div>
            <div className="flex justify-center flex-col items-center">
              <span className="text-n500">Close</span>
              <span className="text-p300 font-medium">{close}</span>
            </div>
          </div>
        </div>

        {/* Market Depth */}
        <div className="px-4 py-4">
          <MarketDepth symbolCode={instrument?.code} />
        </div>

        <div className="px-4 py-3 border-b border-n200">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex justify-between">
              <span className="text-n500">Volume:</span>
              <span className="text-p300 font-medium">
                {formatNumber(marketData.volume) || "0"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-n500">Avg Price:</span>
              <span className="text-p300 font-medium">
                {marketData.ap || "0.0"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-n500">LTQ:</span>
              <span className="text-p300 font-medium">
                {marketData.ltq || "0"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-n500">LTT:</span>
              <span className="text-p300 font-medium">
                {marketData.ltt || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-n500">Expiry:</span>
              <span className="text-p300 font-medium">
                {instrument?.expiry_date || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-n500">OI:</span>
              <span className="text-p300 font-medium">
                {marketData.oi || "0"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-n500">Upper Circuit:</span>
              <span className="text-p300 font-medium">
                {marketData.uc || "0.0"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-n500">Lower Circuit:</span>
              <span className="text-p300 font-medium">
                {marketData.lc || "0.0"}
              </span>
            </div>
          </div>
        </div>

        <div className="px-4 py-4">
          <div className="flex gap-3">
            <button
              className="flex-1 py-2 px-4 rounded-md text-white font-medium bg-p150 hover:bg-p150/90 transition-colors"
              onClick={handleBuyClick}>
              Buy
            </button>
            <button
              className="flex-1 py-2 px-4 rounded-md text-white font-medium bg-red hover:bg-red/90 transition-colors"
              onClick={handleSellClick}>
              Sell
            </button>
          </div>
        </div>
      </div>
      </Draggable>
    </div>
  );
};

export default MarketDepthModal;
